<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSyncStore } from '@/stores/sync'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { usePersonnelStore } from '@/stores/personnel'
import { useTeamsStore } from '@/stores/teams'
import { useSubjectsStore } from '@/stores/subjects'
import { useRadioLogStore } from '@/stores/radioLog'
import { OverheadRole, MessageType, TeamStatus, TeamType } from '@/types'
import SyncIndicator from '../common/SyncIndicator.vue'
import { TooltipRoot, TooltipTrigger, TooltipContent, TooltipArrow } from 'radix-vue'

const appVersion = __APP_VERSION__

const router = useRouter()
const syncStore = useSyncStore()
const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const personnelStore = usePersonnelStore()
const teamsStore = useTeamsStore()
const subjectsStore = useSubjectsStore()
const radioLogStore = useRadioLogStore()

const incident = computed(() => incidentStore.currentIncident)

// Elapsed since incident start
const elapsedSeconds = ref(0)
let elapsedInterval: ReturnType<typeof setInterval> | null = null

function getStartMs(): number | null {
  const inc = incident.value
  if (!inc) return null
  if (inc.start_date) {
    const timeStr = inc.start_time ?? '00:00'
    const dt = new Date(`${inc.start_date}T${timeStr}:00`)
    if (!isNaN(dt.getTime())) return dt.getTime()
  }
  if (inc.created_at) return inc.created_at.toMillis()
  return null
}

function tick() {
  const startMs = getStartMs()
  if (startMs !== null) {
    elapsedSeconds.value = Math.max(0, Math.floor((Date.now() - startMs) / 1000))
  }
}

onMounted(() => {
  tick()
  elapsedInterval = setInterval(tick, 1000)
  startSubjectTicker()
})

onUnmounted(() => {
  if (elapsedInterval) clearInterval(elapsedInterval)
  if (subjectInterval) clearInterval(subjectInterval)
})

const elapsedDisplay = computed(() => {
  const s = elapsedSeconds.value
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
})

// Start time display (e.g. "20 Mar 2026 · 14:32Z")
const startTimeDisplay = computed(() => {
  const inc = incident.value
  if (!inc?.start_date) return null
  const [y, mo, d] = inc.start_date.split('-')
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const monthLabel = monthNames[parseInt(mo ?? '0', 10) - 1] ?? mo
  const datePart = `${parseInt(d ?? '0', 10)} ${monthLabel} ${y}`
  const t = inc.start_time ?? ''
  return t ? `${datePart} · ${t}` : datePart
})

// Current OP label (e.g. "1 · 20 Mar 2026")
const opDisplay = computed(() => {
  const op = opsStore.selectedOp
  if (!op) return null
  if (!op.date) return `${op.op_number}`
  const [y, mo, d] = op.date!.split('-')
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const monthLabel = monthNames[parseInt(mo!, 10) - 1] ?? mo
  return `${op.op_number} · ${parseInt(d!, 10)} ${monthLabel} ${y}`
})

// Last comms entry
const lastComms = computed(() => radioLogStore.entries[0] ?? null)

const lastCommsDisplay = computed(() => {
  const e = lastComms.value
  if (!e) return null
  const isAssignment =
    e.message_type === MessageType.StartingAssignment ||
    e.message_type === MessageType.CompletedAssignment
  let base = `${e.team_identifier} · ${e.message_type}`
  if (isAssignment && e.assignment_name) base += ` — ${e.assignment_name}`
  if (!e.notes) return base
  const truncated = e.notes.length > 40 ? e.notes.slice(0, 40) + '…' : e.notes
  return `${base} · ${truncated}`
})

const lastCommsTime = computed(() => {
  const e = lastComms.value
  if (!e?.client_timestamp) return null
  const d = e.client_timestamp.toDate()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
})

// Resources
const personnelCount = computed(() => personnelStore.onSceneCount)
const teamsInField = computed(() =>
  teamsStore.teams.filter(
    (t) =>
      t.team_type !== TeamType.Overhead &&
      t.team_status !== TeamStatus.AwaitingAssignment &&
      t.team_status !== TeamStatus.Building,
  ).length,
)

function navigateToTeams() {
  const incidentId = incidentStore.currentIncident?.incident_id
  if (incidentId) router.push({ name: 'incident-teams', params: { incidentId } })
}

// Subject ticker
const subjects = computed(() => subjectsStore.subjects)
const subjectCount = computed(() => subjects.value.length)
const tickerIndex = ref(0)
const displayedIndex = ref(0)
let subjectInterval: ReturnType<typeof setInterval> | null = null

function startSubjectTicker() {
  if (subjectInterval) clearInterval(subjectInterval)
  subjectInterval = setInterval(() => {
    if (subjects.value.length > 1) {
      setTimeout(() => {
        tickerIndex.value = (tickerIndex.value + 1) % subjects.value.length
        displayedIndex.value = tickerIndex.value
      }, 200)
    }
  }, 3000)
}

const currentSubject = computed(() => subjects.value[tickerIndex.value] ?? null)

function navigateToSubjects() {
  const incidentId = incidentStore.currentIncident?.incident_id
  if (incidentId) router.push({ name: 'incident-subjects', params: { incidentId } })
}

function navigateToComms() {
  const incidentId = incidentStore.currentIncident?.incident_id
  if (incidentId) router.push({ name: 'incident-comms', params: { incidentId } })
}

// IC name from overhead team user_ops
const icName = computed(() => {
  const icUserOp = teamsStore.userOps.find((uo) => uo.role === OverheadRole.IC)
  if (!icUserOp) return null
  const person = personnelStore.roster.find((p) => p.personnel_id === icUserOp.personnel_id) ?? null
  if (!person) return null
  return `${person.last.toUpperCase()}, ${person.first}`
})
</script>

<template>
  <footer class="hidden md:flex fixed bottom-0 left-0 right-0 h-10 z-50 items-center gap-0 px-4 bg-background border-t border-border">

    <!-- Start time -->
    <template v-if="incident && startTimeDisplay">
      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-[10px] uppercase tracking-[0.06em] text-outline/60 font-display">Start</span>
        <span class="text-[12px] text-on-surface-variant font-display">{{ startTimeDisplay }}</span>
      </div>
      <div class="w-px h-4 bg-border mx-4 shrink-0"></div>
    </template>

    <!-- Elapsed -->
    <template v-if="incident">
      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-[10px] uppercase tracking-[0.06em] text-outline/60 font-display">Elapsed</span>
        <span class="text-[12px] text-primary font-mono">{{ elapsedDisplay }}</span>
      </div>
      <div class="w-px h-4 bg-border mx-4 shrink-0"></div>
    </template>

    <!-- Subject ticker -->
    <template v-if="incident">
      <div class="flex items-center gap-1.5 shrink-0 cursor-pointer hover:opacity-80 transition-opacity overflow-hidden" @click="navigateToSubjects">
        <span class="text-[10px] uppercase tracking-[0.06em] text-outline/60 font-display shrink-0">Subj</span>
        <template v-if="subjectCount > 0">
          <div class="relative overflow-hidden h-[1.2em]">
            <Transition name="ticker">
              <span :key="tickerIndex" class="flex items-center gap-1.5 whitespace-nowrap text-[12px] text-on-surface-variant">
                <template v-if="currentSubject">
                  <span class="font-display">{{ currentSubject.first_name }} {{ currentSubject.last_name }}</span>
                  <template v-if="currentSubject.subject_category">
                    <span class="font-display">·</span>
                    <span>{{ currentSubject.subject_category }}</span>
                  </template>
                  <span class="font-display">·</span>
                  <span>{{ currentSubject.age ? `${currentSubject.age}YO` : '' }}{{ currentSubject.gender ? currentSubject.gender.charAt(0).toUpperCase() : '?' }}</span>
                  <template v-if="currentSubject.risk_category">
                    <span class="font-display">·</span>
                    <span>{{ currentSubject.risk_category }}</span>
                  </template>
                  <template v-if="subjectCount > 1">
                    <span class="text-outline/60">·</span>
                    <span class="text-outline/40">{{ displayedIndex + 1 }}/{{ subjectCount }}</span>
                  </template>
                </template>
              </span>
            </Transition>
          </div>
        </template>
        <template v-else>
          <span class="text-[12px] font-mono text-outline/40">none</span>
        </template>
      </div>
      <div class="w-px h-4 bg-border mx-4 shrink-0"></div>
    </template>

    <!-- Resources: personnel + teams -->
    <template v-if="incident">
      <div class="flex items-center gap-1.5 shrink-0 cursor-pointer hover:opacity-80 transition-opacity" @click="navigateToTeams">
        <span class="text-[10px] uppercase tracking-[0.06em] text-outline/60 font-display">Pers</span>
        <span class="text-[12px] font-mono text-on-surface-variant">{{ personnelCount }}</span>
        <span class="text-[10px] uppercase tracking-[0.06em] text-outline/60 font-display ml-1">Teams</span>
        <span class="text-[12px] font-mono text-on-surface-variant">{{ teamsInField }}</span>
      </div>
      <div class="w-px h-4 bg-border mx-4 shrink-0"></div>
    </template>

    <!-- Last comms -->
    <template v-if="lastCommsDisplay">
      <div class="flex items-center gap-1.5 shrink-0 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" @click="navigateToComms">
        <span class="text-[10px] uppercase tracking-[0.06em] text-outline/60 font-display shrink-0">Last Comms</span>
        <span class="text-[12px] text-on-surface-variant font-display truncate max-w-[280px]">{{ lastCommsDisplay }}</span>
        <span class="text-[12px] font-mono text-outline/60">{{ lastCommsTime }}</span>
      </div>
      <div class="w-px h-4 bg-border mx-4 shrink-0"></div>
    </template>

    <!-- Spacer -->
    <div class="flex-1"></div>

    <!-- Sync state + info -->
    <div class="flex items-center gap-1.5 shrink-0">
      <SyncIndicator/>
      <TooltipRoot>
        <TooltipTrigger as-child>
          <button
            class="w-4 h-4 flex items-center justify-center text-outline/40 hover:text-outline transition-colors ml-0.5 shrink-0"
            @click="router.push({ name: 'changelog' })"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5">
              <path fill-rule="evenodd" d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0V8.75A.75.75 0 0 0 8.25 8h-1.5Z" clip-rule="evenodd" />
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="end"
          class="bg-surface-container-highest border border-border rounded px-3 py-2 z-[100] shadow-sm"
        >
          <p class="font-mono text-[11px] text-on-surface">v{{ appVersion }}</p>
          <p class="font-display text-[10px] text-outline/70 mt-0.5">© 2026 First On, LLC. All rights reserved.</p>
          <TooltipArrow class="fill-border" />
        </TooltipContent>
      </TooltipRoot>
    </div>
  </footer>
</template>

<style scoped>
.ticker-enter-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.ticker-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
  position: absolute;
  top: 0;
  left: 0;
}
.ticker-enter-from {
  transform: translateY(100%);
  opacity: 0;
}
.ticker-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
